import React, { useEffect, useState } from 'react';
import { useStudentNamesSpoken, useStudentInteraction } from 'hooks';
import './styles.css';

interface StudentNamesPanelProps {
  classId: number | string;
}

const StudentNamesPanel: React.FC<StudentNamesPanelProps> = ({ classId }) => {
  // Set polling interval to 10 seconds (10000ms)
  const { data: namesSpokenData, isLoading: namesLoading, error: namesError, refetch } = useStudentNamesSpoken(classId, 10000);
  const { interactionData, isLoading: interactionLoading } = useStudentInteraction(classId, 10000);
  
  // Track data changes
  useEffect(() => {
    // Data updated
  }, [namesSpokenData]);
  
  // Track interaction data changes
  useEffect(() => {
    // Data updated
  }, [interactionData, namesSpokenData]);

  // Try to get student names from localStorage if available
  const [studentNames, setStudentNames] = useState<Record<string, string>>({});
  
  useEffect(() => {
    // Check if we have student names in localStorage
    const studentNamesKey = `studentNames_${classId}`;
    const storedStudentNames = localStorage.getItem(studentNamesKey);
    
    if (storedStudentNames) {
      try {
        const parsedNames = JSON.parse(storedStudentNames);
        setStudentNames(parsedNames);
      } catch (err) {
        console.error('Error parsing stored student names:', err);
      }
    }
  }, [classId]);
  
  // Extract students from the interaction data
  // If we have fifteenMinuteWindows, get students from the first window
  // Otherwise, fall back to an empty array
  const students = interactionData?.fifteenMinuteWindows?.[0]?.studentParticipation || [];

  // Early return for loading states
  if (namesLoading || interactionLoading) {
    return <div className="student-names-panel loading">Loading...</div>;
  }

  // Only show error if we have a specific error from the names API
  // Don't show error just because interactionData is missing
  if (namesError) {
    return <div className="student-names-panel error">Unable to load data</div>;
  }

  // If no students, show empty state
  if (students.length === 0) {
    // If we have namesSpokenData but no students, we can still show the data
    if (Object.keys(namesSpokenData).length > 0) {
      // Create mock students from the namesSpokenData
      const mockStudents = Object.keys(namesSpokenData).map(studentId => ({
        studentId: parseInt(studentId, 10),
        // Use the firstName from the API response if available
        studentName: namesSpokenData[studentId]?.firstName || studentNames[studentId] || `Student ${studentId}`,
        wordCount: 0,
        participationPercentage: 0
      }));
      
      // Use these mock students instead
      return (
        <div className="student-names-panel">
          <div className="student-names-list">
            {mockStudents.map((student) => {
              const studentData = namesSpokenData[student.studentId];
              const isNameSpoken = studentData?.nameWasSpoken || false;
              
              return (
                <div key={student.studentId} className="student-name-item">
                  <div className="student-name">
                    {/* Try to get a real student name from the LMS if available */}
                    {student.studentName.startsWith('Student ') 
                      ? `Student ${student.studentId}` 
                      : student.studentName}
                  </div>
                  <div className={`name-status ${isNameSpoken ? 'spoken' : 'not-spoken'}`}>
                    {isNameSpoken ? (
                      <span className="status-icon">✓</span>
                    ) : (
                      <span className="status-icon">○</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Debug info - only visible in development */}
          {process.env.NODE_ENV === 'development' && (
            <div className="debug-info" style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', marginTop: '4px', textAlign: 'center' }}>
              <button 
                onClick={() => {
                  refetch();
                }}
                style={{ 
                  background: 'rgba(0,0,0,0.3)', 
                  border: 'none', 
                  color: 'white', 
                  padding: '2px 4px', 
                  fontSize: '10px',
                  borderRadius: '2px'
                }}
              >
                Debug Refresh
              </button>
            </div>
          )}
        </div>
      );
    }
    
    return <div className="student-names-panel empty">No students</div>;
  }

  return (
    <div className="student-names-panel">
      <div className="student-names-list">
        {students.map((student) => {
          const studentData = namesSpokenData[student.studentId];
          const isNameSpoken = studentData?.nameWasSpoken || false;
          
          // Use firstName from API response if available, then stored name, then interaction data
          const displayName = studentData?.firstName || studentNames[student.studentId] || student.studentName;
          
          return (
            <div key={student.studentId} className="student-name-item">
              <div className="student-name">{displayName}</div>
              <div className={`name-status ${isNameSpoken ? 'spoken' : 'not-spoken'}`}>
                {isNameSpoken ? (
                  <span className="status-icon">✓</span>
                ) : (
                  <span className="status-icon">○</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Debug info - only visible in development */}
      {process.env.NODE_ENV === 'development' && (
        <div className="debug-info" style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', marginTop: '4px', textAlign: 'center' }}>
          <button 
            onClick={() => {
              refetch();
            }}
            style={{ 
              background: 'rgba(0,0,0,0.3)', 
              border: 'none', 
              color: 'white', 
              padding: '2px 4px', 
              fontSize: '10px',
              borderRadius: '2px'
            }}
          >
            Debug Refresh
          </button>
        </div>
      )}
    </div>
  );
};

export default StudentNamesPanel;